import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLang, tx } from "@/context/LangContext";
import { T } from "@/data/content";
import { FadeUp, SectionLabel } from "@/components/Reveal";
import { music } from "@/lib/player";
import { Music2, Play, Search, Send, Pause, Loader2, ExternalLink, MonitorPlay, Radio as RadioIcon, Disc3 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

/* petites barres d'égaliseur animées — piste en cours de lecture */
const EqBars = ({ className = "text-violet" }) => (
    <span className={`flex items-end gap-[3px] ${className}`} aria-hidden>
        {[0, 1, 2].map((i) => (
            <motion.span
                key={i}
                className="w-[3px] rounded-full bg-current"
                animate={{ height: [4, 14, 6, 16, 5] }}
                transition={{ duration: 0.9, repeat: Infinity, delay: i * 0.15, ease: "easeInOut" }}
            />
        ))}
    </span>
);

const rowStagger = {
    hidden: { opacity: 0, x: -24 },
    show: (i) => ({
        opacity: 1,
        x: 0,
        transition: { delay: 0.03 * i, duration: 0.5, ease: [0.16, 1, 0.3, 1] },
    }),
};

export default function Radio() {
    const { lang } = useLang();
    const [q, setQ] = useState("");
    const [results, setResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [approved, setApproved] = useState([]);
    const [form, setForm] = useState({ artist: "", title: "", link: "", spotify: "", contact: "" });
    const [sent, setSent] = useState(false);
    const [player, setPlayer] = useState(music.state);

    const [tracks, setTracks] = useState([]);
    useEffect(() => music.subscribe(setPlayer), []);
    useEffect(() => {
        fetch(`${API}/api/radio/tracks`)
            .then((r) => r.json())
            .then((d) => setApproved(d.tracks || []))
            .catch(() => {});
    }, [sent]);
    const [swissTracks, setSwissTracks] = useState([]);
    const [nowPlaying, setNowPlaying] = useState(null); // vidéo affichée
    useEffect(() => {
        fetch(`${API}/api/radio/spotify`)
            .then((r) => r.json())
            .then((d) => setTracks(d.tracks || []))
            .catch(() => {});
    }, []);
    useEffect(() => {
        fetch(`${API}/api/radio/swiss-tracks`)
            .then((r) => r.json())
            .then((d) => setSwissTracks(d.tracks || []))
            .catch(() => {});
    }, []);

    const search = async () => {
        if (!q.trim()) return;
        setSearching(true);
        try {
            const r = await fetch(`${API}/api/radio/search?q=${encodeURIComponent(q.trim())}`);
            const d = await r.json();
            setResults(d.results || []);
        } finally {
            setSearching(false);
        }
    };

    const submit = async () => {
        const r = await fetch(`${API}/api/radio/submit`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(form),
        });
        if (r.ok) {
            setSent(true);
            setForm({ artist: "", title: "", link: "", spotify: "", contact: "" });
        }
    };

    const playYt = (t) => music.playSpecific({ type: "yt", id: t.id, title: `${t.artist ? `${t.artist} — ` : ""}${t.title}`, cover: t.thumb });

    // lit une vidéo : lecteur vidéo visible via l'API YouTube Player. Le son démarre au clic.
    const playVideo = (t) => {
        setVideoBlocked(false);
        setNowPlaying(t);
        setTimeout(() => music.openVideo({ type: "yt-video", id: t.id, title: t.title, cover: t.cover }, "radio-video-mount"), 60);
    };

    // fermer la vidéo → le son continue en arrière-plan
    const closeVideo = () => {
        music.closeVideo(true);
        setNowPlaying(null);
    };

    // si on quitte la page radio pendant la lecture vidéo → le son continue en arrière-plan
    useEffect(() => {
        return () => {
            if (music.videoYt) music.closeVideo(true);
        };
    }, []);

    // YouTube refuse la lecture intégrée de certains clips (droits musicaux) → fallback propre
    const [videoBlocked, setVideoBlocked] = useState(false);
    useEffect(() => {
        const onErr = () => setVideoBlocked(true);
        window.addEventListener("music:video-error", onErr);
        return () => window.removeEventListener("music:video-error", onErr);
    }, []);
    const spotifyUrl = (id) => `https://open.spotify.com/track/${id}`;

    const isCurrent = (t) => player.title && t.title && player.title.includes(t.title.slice(0, 12)) && player.status === "playing";

    return (
        <section data-testid="radio-page" className="relative min-h-screen overflow-hidden bg-ink pb-24 text-cream">
            <div
                className="pointer-events-none absolute inset-0 opacity-30"
                style={{ background: "radial-gradient(60% 50% at 75% 10%, rgba(138,43,226,0.22) 0%, transparent 80%)" }}
            />

            {/* bandeau défilant — identité radio */}
            <div className="relative mt-[72px] overflow-hidden border-y border-white/10 bg-ink/80 py-2.5 backdrop-blur-md">
                <div className="animate-marquee flex w-max whitespace-nowrap">
                    {[0, 1].map((k) => (
                        <span key={k} className="flex items-center font-mono text-[10px] tracking-[0.4em] text-violet/80">
                            {Array.from({ length: 6 }).map((_, i) => (
                                <span key={i} className="flex items-center">
                                    <span className="mx-6">GLM RADIO</span>
                                    <span className="text-gold">•</span>
                                    <span className="mx-6">{lang === "fr" ? "SONS AUDIOVISUELS SUISSES" : "SWISS AUDIOVISUAL SOUNDS"}</span>
                                    <span className="text-gold">•</span>
                                    <span className="mx-6">GENÈVE</span>
                                    <span className="text-gold">•</span>
                                </span>
                            ))}
                        </span>
                    ))}
                </div>
            </div>

            {/* lecteur vidéo flottant — glass, le clip YouTube visible pendant la lecture */}
            <AnimatePresence>
                {nowPlaying && (
                    <motion.div
                        data-testid="radio-video-box"
                        initial={{ opacity: 0, y: 40, scale: 0.96 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 40, scale: 0.96 }}
                        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                        className="fixed bottom-24 right-5 z-[70] w-[320px] overflow-hidden border border-violet/40 bg-ink/90 shadow-[0_25px_70px_rgba(0,0,0,0.85)] backdrop-blur-xl md:right-10 md:w-[440px]"
                    >
                        <div className="flex items-center justify-between border-b border-white/10 px-3 py-2">
                            <p className="flex items-center gap-2 truncate font-mono text-[9px] tracking-[0.2em] text-violet">
                                {player.status === "playing" ? <EqBars /> : <MonitorPlay size={12} className="shrink-0" />}
                                {nowPlaying.title || "GLOOM RADIO"}
                            </p>
                            <button data-testid="radio-video-close" onClick={closeVideo} aria-label="Fermer la vidéo" className="ml-2 shrink-0 text-cream/50 transition-colors hover:text-cream">✕</button>
                        </div>
                        <div className="relative aspect-video bg-black">
                            <div id="radio-video-mount" className="absolute inset-0" />
                            {videoBlocked && (
                                <div data-testid="radio-video-blocked" className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-ink p-6 text-center">
                                    <MonitorPlay size={22} className="text-gold" />
                                    <p className="font-mono text-[9px] leading-relaxed tracking-[0.2em] text-cream/70">
                                        {lang === "fr" ? "LECTURE INTÉGRÉE BLOQUÉE PAR YOUTUBE (DROITS DU CLIP)" : "EMBEDDED PLAYBACK BLOCKED BY YOUTUBE (CLIP RIGHTS)"}
                                    </p>
                                    <a
                                        data-testid="radio-video-youtube-link"
                                        href={`https://youtu.be/${nowPlaying.id}`}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="flex items-center gap-2 border border-gold bg-gold/10 px-5 py-2.5 font-mono text-[9px] tracking-[0.25em] text-gold transition-all hover:bg-gold hover:text-ink"
                                    >
                                        <ExternalLink size={12} /> {lang === "fr" ? "REGARDER SUR YOUTUBE" : "WATCH ON YOUTUBE"}
                                    </a>
                                </div>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            <div className="relative mx-auto max-w-[1200px] px-5 pt-16 md:px-10">
                <SectionLabel en={tx(lang, T.radio.label)} fr={tx(lang, T.radio.label)} />
                <FadeUp delay={0.1}>
                    <div className="mt-8 flex flex-wrap items-end justify-between gap-6">
                        <h1 className="font-display text-[clamp(3.2rem,9vw,7.5rem)] font-extrabold uppercase leading-[0.9] tracking-[-0.03em]">
                            {tx(lang, T.radio.title)}
                            <span className="text-violet">.</span>
                        </h1>
                        {/* badge live avec égaliseur */}
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: 0.4 }}
                            className="flex items-center gap-3 border border-violet/40 bg-violet/10 px-4 py-2.5 backdrop-blur-md"
                        >
                            <EqBars />
                            <span className="font-mono text-[9px] tracking-[0.3em] text-violet">
                                {lang === "fr" ? "EN DIRECT DE GENÈVE" : "LIVE FROM GENEVA"}
                            </span>
                        </motion.div>
                    </div>
                    <p className="mt-4 max-w-lg font-mono text-[10px] leading-relaxed tracking-[0.3em] text-cream/40">
                        {tx(lang, T.radio.note)}
                    </p>
                </FadeUp>

                {/* search — barre glass */}
                <FadeUp delay={0.15} className="mt-12">
                    <div className="flex gap-3 border border-white/10 bg-white/[0.03] p-3 shadow-[0_10px_30px_rgba(0,0,0,0.5)] backdrop-blur-md transition-all duration-300 focus-within:border-violet hover:border-violet/50">
                        <input
                            data-testid="radio-search"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && search()}
                            placeholder={tx(lang, T.radio.searchPlaceholder)}
                            className="w-full bg-transparent px-3 font-mono text-[11px] tracking-[0.15em] text-cream outline-none placeholder:text-cream/30"
                        />
                        <button
                            data-testid="radio-search-btn"
                            onClick={search}
                            className="flex items-center gap-2 border border-violet/60 px-5 py-2 font-mono text-[9px] tracking-[0.25em] text-violet transition-all hover:bg-violet hover:text-cream"
                        >
                            {searching ? <Loader2 size={12} className="animate-spin" /> : <Search size={12} />}
                        </button>
                    </div>
                    <AnimatePresence>
                        {results.length > 0 && (
                            <motion.div
                                initial={{ opacity: 0, y: 16 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: 16 }}
                                className="mt-6 grid grid-cols-2 gap-4 md:grid-cols-4"
                            >
                                {results.map((t, i) => (
                                    <motion.button
                                        key={t.id}
                                        custom={i}
                                        variants={rowStagger}
                                        initial="hidden"
                                        animate="show"
                                        data-testid={`radio-result-${t.id}`}
                                        onClick={() => playYt(t)}
                                        className={`group border text-left transition-colors ${isCurrent(t) ? "border-violet/60 bg-violet/[0.06]" : "border-white/10 hover:border-violet"}`}
                                    >
                                        <div className="relative aspect-video overflow-hidden">
                                            <img src={t.thumb} alt={t.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                                            <span className="absolute inset-0 flex items-center justify-center bg-ink/40 opacity-0 transition-opacity group-hover:opacity-100">
                                                {isCurrent(t) ? <Pause size={20} className="text-cream" /> : <Play size={20} className="text-cream" />}
                                            </span>
                                            {isCurrent(t) && (
                                                <span className="absolute bottom-2 left-2"><EqBars /></span>
                                            )}
                                        </div>
                                        <p className="truncate p-3 font-mono text-[9px] tracking-[0.15em] text-cream/70">{t.title}</p>
                                        <p className="truncate px-3 pb-3 font-mono text-[8px] tracking-[0.2em] text-cream/35">{t.channel}</p>
                                    </motion.button>
                                ))}
                            </motion.div>
                        )}
                    </AnimatePresence>
                </FadeUp>

                {/* playlist curée GLOOM — liste éditoriale numérotée */}
                {swissTracks.length > 0 && (
                    <FadeUp delay={0.12} className="mt-16">
                        <div className="flex items-center justify-between">
                            <p className="flex items-center gap-2 font-mono text-[10px] tracking-[0.35em] text-violet">
                                <MonitorPlay size={13} /> {lang === "fr" ? "LA PLAYLIST GLOOM — CLIPS & SONS" : "THE GLOOM PLAYLIST — CLIPS & TRACKS"}
                            </p>
                            <p className="font-mono text-[9px] tracking-[0.25em] text-cream/30">{swissTracks.length} {lang === "fr" ? "TITRES" : "TRACKS"}</p>
                        </div>
                        <div className="mt-5 space-y-2">
                            {swissTracks.map((t, i) => {
                                const active = nowPlaying?.id === t.id;
                                return (
                                    <motion.button
                                        key={t.id}
                                        custom={i}
                                        variants={rowStagger}
                                        initial="hidden"
                                        whileInView="show"
                                        viewport={{ once: true, margin: "-40px" }}
                                        data-testid={`radio-pick-${t.id}`}
                                        onClick={() => playVideo(t)}
                                        className={`group relative flex w-full items-center gap-4 overflow-hidden border p-3 text-left transition-all duration-300 sm:p-4 ${
                                            active
                                                ? "border-violet/60 bg-violet/[0.08]"
                                                : "border-white/5 bg-white/[0.02] hover:border-violet/50 hover:bg-white/[0.05]"
                                        }`}
                                    >
                                        {/* numéro éditorial */}
                                        <span className={`w-12 shrink-0 self-start pt-0.5 font-display text-2xl font-extrabold transition-colors duration-300 sm:w-14 sm:text-3xl ${active ? "text-violet" : "text-white/20 group-hover:text-violet"}`}>
                                            {String(i + 1).padStart(2, "0")}
                                        </span>
                                        {/* titre */}
                                        <span className="min-w-0 flex-1">
                                            <span className={`block truncate font-sans text-sm font-medium transition-colors sm:text-base ${active ? "text-cream" : "text-cream/90 group-hover:text-white"}`}>
                                                {t.title || t.artist || "GLOOM"}
                                            </span>
                                            <span className="mt-0.5 block truncate font-mono text-[9px] uppercase tracking-[0.18em] text-cream/40">
                                                {lang === "fr" ? "CLIP + SON" : "CLIP + TRACK"}
                                            </span>
                                        </span>
                                        {/* état lecture / icône */}
                                        <span className="flex shrink-0 items-center gap-3">
                                            {active && player.status === "playing" ? (
                                                <EqBars />
                                            ) : (
                                                <span className="flex h-9 w-9 items-center justify-center rounded-full border border-white/15 text-cream/60 transition-all duration-300 group-hover:border-violet group-hover:bg-violet group-hover:text-cream">
                                                    <Play size={13} className="ml-0.5" />
                                                </span>
                                            )}
                                        </span>
                                        {/* miniature révélée au hover (desktop) */}
                                        <span className="pointer-events-none absolute right-24 top-1/2 hidden w-40 -translate-y-1/2 rotate-3 opacity-0 shadow-[0_15px_40px_rgba(0,0,0,0.6)] transition-all duration-300 group-hover:rotate-0 group-hover:opacity-100 md:block">
                                            <img src={t.cover} alt="" className="aspect-video w-full border border-white/20 object-cover" loading="lazy" />
                                        </span>
                                        {/* liseré violet à gauche */}
                                        <span className={`absolute left-0 top-0 h-full w-[3px] bg-violet transition-transform duration-300 ${active ? "scale-y-100" : "scale-y-0 group-hover:scale-y-100"}`} style={{ transformOrigin: "top" }} />
                                    </motion.button>
                                );
                            })}
                        </div>
                    </FadeUp>
                )}

                {/* approved artists — édition or */}
                {approved.length > 0 && (
                    <FadeUp delay={0.1} className="mt-16">
                        <p className="flex items-center gap-2 font-mono text-[10px] tracking-[0.35em] text-gold">
                            <Disc3 size={13} /> {tx(lang, T.radio.playing)}
                        </p>
                        <div className="mt-5 grid grid-cols-2 gap-5 md:grid-cols-4">
                            {approved.map((t, i) => (
                                <motion.div
                                    key={t.submission_id}
                                    custom={i}
                                    variants={rowStagger}
                                    initial="hidden"
                                    whileInView="show"
                                    viewport={{ once: true }}
                                    className="border border-gold/30 bg-gold/[0.04] transition-colors hover:border-gold"
                                >
                                    <button
                                        data-testid={`radio-approved-${t.submission_id}`}
                                        onClick={() => playYt({ ...t, id: t.link.split("v=")[1]?.split("&")[0] || t.link.split("/").pop() })}
                                        className="group w-full text-left"
                                    >
                                        <div className="relative aspect-square overflow-hidden">
                                            <img src={`https://img.youtube.com/vi/${t.link.split("v=")[1]?.split("&")[0] || t.link.split("/").pop()}/hqdefault.jpg`} alt={t.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105 group-hover:rotate-1" />
                                            <span className="absolute inset-0 flex items-center justify-center bg-ink/40 opacity-0 transition-opacity group-hover:opacity-100">
                                                <Play size={22} className="text-cream" />
                                            </span>
                                        </div>
                                        <p className="truncate p-3 font-mono text-[9px] tracking-[0.15em] text-gold/90">{t.artist}</p>
                                        <p className="truncate px-3 pb-3 font-mono text-[8px] tracking-[0.2em] text-cream/35">{t.title}</p>
                                    </button>
                                    {t.spotify && (
                                        <a
                                            data-testid={`spotify-${t.submission_id}`}
                                            href={t.spotify}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="flex items-center justify-center gap-2 border-t border-gold/20 px-3 py-2 font-mono text-[8px] tracking-[0.2em] text-gold/80 transition-colors hover:bg-gold hover:text-ink"
                                        >
                                            <ExternalLink size={11} /> SPOTIFY
                                        </a>
                                    )}
                                </motion.div>
                            ))}
                        </div>
                    </FadeUp>
                )}

                {/* spotify tracks */}
                {tracks.length > 0 && (
                    <FadeUp delay={0.1} className="mt-16">
                        <p className="font-mono text-[10px] tracking-[0.35em] text-violet">{tx(lang, T.radio.spotifyTitle)}</p>
                        <p className="mt-2 font-mono text-[9px] tracking-[0.25em] text-cream/40">{tx(lang, T.radio.spotifyNote)}</p>
                        <div className="mt-5 grid grid-cols-2 gap-4 md:grid-cols-4">
                            {tracks.map((t, i) => (
                                <motion.a
                                    key={t.id}
                                    custom={i}
                                    variants={rowStagger}
                                    initial="hidden"
                                    whileInView="show"
                                    viewport={{ once: true }}
                                    data-testid={`spotify-track-${t.id}`}
                                    href={spotifyUrl(t.id)}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="group border border-white/10 bg-white/[0.02] text-left transition-colors hover:border-emerald-400/60"
                                >
                                    <div className="relative aspect-square overflow-hidden">
                                        <img src={t.cover} alt={t.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                                        <span className="absolute inset-0 flex items-center justify-center bg-ink/40 opacity-0 transition-opacity group-hover:opacity-100">
                                            <ExternalLink size={20} className="text-cream" />
                                        </span>
                                    </div>
                                    <p className="truncate p-3 font-mono text-[9px] tracking-[0.15em] text-cream/70">{t.title}</p>
                                    <p className="truncate px-3 pb-3 font-mono text-[8px] tracking-[0.2em] text-cream/35">{t.artist}</p>
                                </motion.a>
                            ))}
                        </div>
                    </FadeUp>
                )}

                {/* submit — split éditorial */}
                <FadeUp delay={0.1} className="mt-20 border-t border-white/10 pt-14">
                    <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
                        <div className="lg:col-span-5">
                            <p className="flex items-center gap-3 font-mono text-[10px] tracking-[0.3em] text-violet">
                                <RadioIcon size={14} /> {tx(lang, T.radio.submitTitle)}
                            </p>
                            <p className="mt-4 max-w-sm font-mono text-[9px] leading-relaxed tracking-[0.25em] text-cream/40">
                                {tx(lang, T.radio.submitNote)}
                            </p>
                        </div>
                        <div className="lg:col-span-7">
                            {!sent ? (
                                <div className="space-y-4">
                                    <input data-testid="radio-artist" value={form.artist} onChange={(e) => setForm({ ...form, artist: e.target.value })} placeholder={tx(lang, T.radio.artist)} className="w-full border-b border-white/15 bg-transparent py-2 font-mono text-[11px] tracking-[0.15em] text-cream outline-none transition-colors placeholder:text-cream/30 focus:border-violet" />
                                    <input data-testid="radio-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder={tx(lang, T.radio.trackTitle)} className="w-full border-b border-white/15 bg-transparent py-2 font-mono text-[11px] tracking-[0.15em] text-cream outline-none transition-colors placeholder:text-cream/30 focus:border-violet" />
                                    <input data-testid="radio-link" value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })} placeholder={tx(lang, T.radio.link)} className="w-full border-b border-white/15 bg-transparent py-2 font-mono text-[11px] tracking-[0.15em] text-cream outline-none transition-colors placeholder:text-cream/30 focus:border-violet" />
                                    <input data-testid="radio-spotify" value={form.spotify} onChange={(e) => setForm({ ...form, spotify: e.target.value })} placeholder={tx(lang, T.radio.spotify)} className="w-full border-b border-white/15 bg-transparent py-2 font-mono text-[11px] tracking-[0.15em] text-cream outline-none transition-colors placeholder:text-cream/30 focus:border-violet" />
                                    <input data-testid="radio-contact" value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} placeholder={tx(lang, T.radio.contact)} className="w-full border-b border-white/15 bg-transparent py-2 font-mono text-[11px] tracking-[0.15em] text-cream outline-none transition-colors placeholder:text-cream/30 focus:border-violet" />
                                    <button
                                        data-testid="radio-submit-btn"
                                        onClick={submit}
                                        disabled={!form.artist || !form.title || !form.link}
                                        className="flex items-center gap-2 border border-gold/50 px-6 py-3 font-mono text-[10px] tracking-[0.3em] text-gold transition-all hover:bg-gold hover:text-ink disabled:opacity-30"
                                    >
                                        <Send size={12} /> {tx(lang, T.radio.submitBtn)}
                                    </button>
                                </div>
                            ) : (
                                <motion.p data-testid="radio-sent" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="font-mono text-[10px] tracking-[0.3em] text-gold">
                                    {tx(lang, T.radio.sent)}
                                </motion.p>
                            )}
                        </div>
                    </div>
                </FadeUp>
            </div>
        </section>
    );
}
